import cn from 'classnames';
import { memo, useEffect, useReducer, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useCloseOnEscape, useScrollLock, useToggle } from '@react-md/utils';
import { selectContentCustomization } from '@modules/app';
import { selectWallThicknessFileUrl } from '@modules/wall-thickness';
import { viewerActions, selectWtIsActive } from '@modules/model-viewer';
import {
    ModelViewer as ModelViewerUtil,
    ModelViewerObjects,
    ModelViewerRenderMode,
    customVariables as CV,
} from '@utils';
import { FeatureKeys as FF, getFeatureFlag, getFeatureFlags } from '@components/feature-flags';
import { ObjectModelModel } from '@models';
import { selectIqtModeOn } from '@modules/user';
import { TechnologyMaterial } from '@services/df/technologies';
import { ViewerModePanel } from '../viewer-mode-panel';
import { ViewerThumb } from '../viewer-thumb';
import { ModelViewerDarkModeBox } from './model-viewer-dark-mode-box';
import { ModelViewerFullscreenTopPanel } from './model-viewer-fullscreen-top-panel';
import { ModelViewerFullscreenBottomPanel } from './model-viewer-fullscreen-bottom-panel';
import { ModelViewerSceneControlsPanel } from './model-viewer-scene-controls-panel';
import { viewerPropsAreEqual } from '../helpers';
import { ModelViewerRotationDescription } from './model-viewer-rotation-description';

import styles from '../viewer-window.module.scss';

export interface ModelViewerProps {
    correctObjectModelId?: number;
    model: ObjectModelModel;
    material?: TechnologyMaterial;
    onLoadFileCallback: (modelFileUrl: string) => void;
}

export const ModelViewer = memo<ModelViewerProps>(({ correctObjectModelId, model, material, onLoadFileCallback }) => {
    let visualizer: HTMLDivElement;
    const dispatch = useAppDispatch();
    const wallThicknessFileUrl = useAppSelector(selectWallThicknessFileUrl);
    const wtIsActive = useAppSelector(selectWtIsActive);
    const contentCustomization = useAppSelector(selectContentCustomization);
    const isIqtMode = useAppSelector(selectIqtModeOn);

    const lastRenderedModelId = useRef<number>();
    const modelViewer = useRef<ModelViewerUtil>();

    const [currentMode, setCurrentMode] = useState<ModelViewerRenderMode>(ModelViewerRenderMode.Solid);
    const [isRotationModeActive, enterRotationMode, exitRotationMode] = useToggle(false);
    const [isFullscreenModeActive, enterFullscreenMode, exitFullscreenMode, toggleFullscreenMode] = useToggle(false);
    const [isThumbModeOn, enterThumbMode, exitThumbMode, toggleThumbMode] = useToggle(false);
    const [isDarkModeActive, toggleDarkMode] = useReducer(
        previous => !previous,
        undefined,
        () => getFeatureFlag(contentCustomization, FF.Viewer.DarkModeOn),
    );
    const { id: modelId, title, file_model_viewer_url, allowToRenderViewer } = model;

    useScrollLock(isFullscreenModeActive);

    useEffect(() => {
        modelViewer.current?.requestRenderIfNotRequested();

        if (!isFullscreenModeActive) {
            exitRotationMode();
        }
    }, [isFullscreenModeActive, exitRotationMode]);

    useEffect(() => {
        if (isRotationModeActive) {
            setCurrentMode(ModelViewerRenderMode.Solid);
            enterFullscreenMode();
            exitThumbMode();
        } else {
            exitFullscreenMode();
        }

        if (modelViewer.current) {
            modelViewer.current.rotateModeOn = isRotationModeActive;
        }
    }, [isRotationModeActive, enterFullscreenMode, exitFullscreenMode, exitThumbMode]);

    useEffect(() => {
        const printerSize = material && material.printer_size;

        modelViewer.current = new ModelViewerUtil(visualizer, {
            darkModeOn: isDarkModeActive,
            debug: false,
            backgroundColor: CV.SceneBackgroundColor,
            model: {
                color: CV.ModelColor,
            },
            objects: {
                [ModelViewerObjects.Axes]: {
                    enabled: getFeatureFlags(contentCustomization, [FF.Viewer.AxesShown, FF.Viewer.AxesBtnShown]).some(
                        Boolean,
                    ),
                    active: getFeatureFlag(contentCustomization, FF.Viewer.AxesShown),
                },
                [ModelViewerObjects.Grid]: {
                    enabled: getFeatureFlags(contentCustomization, [
                        FF.Viewer.GridFloorBtnShown,
                        FF.Viewer.GridFloorShown,
                    ]).some(Boolean),
                    active: getFeatureFlag(contentCustomization, FF.Viewer.GridFloorShown),
                },
                [ModelViewerObjects.ModelBoundingBox]: {
                    enabled: getFeatureFlags(contentCustomization, [
                        FF.Viewer.ModelBoxShown,
                        FF.Viewer.ModelBoxBtnShown,
                    ]).some(Boolean),
                    active: getFeatureFlag(contentCustomization, FF.Viewer.ModelBoxShown),
                    color: CV.ModelBoundingBoxColor,
                },
                [ModelViewerObjects.PrinterBoundingBox]: {
                    enabled: getFeatureFlags(contentCustomization, [
                        FF.Viewer.PrinterBoxShown,
                        FF.Viewer.PrinterBoxBtnShown,
                    ]).some(Boolean),
                    active: getFeatureFlag(contentCustomization, FF.Viewer.PrinterBoxShown),
                    // size: '50-23-50',
                    color: CV.PrinterBoundingBoxColor,
                    size: printerSize,
                },
                [ModelViewerObjects.ModelEdges]: {
                    color: CV.ModelEdgesColor,
                },
            },
        });

        return () => {
            modelViewer.current?.clear();
            modelViewer.current = undefined;
            dispatch(viewerActions.setModelIsRendered(false));
            model.setIsModelRendered(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // update printer box
    useEffect(() => {
        const printerSize = material && material.printer_size;
        if (modelViewer.current && printerSize) {
            const printerBoundingBox = modelViewer.current.objects[ModelViewerObjects.PrinterBoundingBox];
            printerBoundingBox?.object?.updateSize(printerSize);
            modelViewer.current.requestRenderIfNotRequested(); // todo move to updateSize?
        }
    }, [material]);

    // load & setup model
    useEffect(() => {
        if (
            !allowToRenderViewer ||
            !modelViewer.current ||
            !onLoadFileCallback ||
            modelId === lastRenderedModelId.current
        )
            return;

        const { promise, cancel } = modelViewer.current.loadModel(file_model_viewer_url, model.getViewerOptions());

        promise.then(() => {
            lastRenderedModelId.current = modelId;
            onLoadFileCallback(file_model_viewer_url);
            dispatch(viewerActions.setModelIsRendered(true));
            model.setIsModelRendered(true);
        });

        return () => {
            cancel();
        };
    }, [
        dispatch,
        model,
        modelId,
        onLoadFileCallback,
        correctObjectModelId,
        allowToRenderViewer,
        file_model_viewer_url,
    ]);

    // preload walls
    useEffect(() => {
        if (!wallThicknessFileUrl || !modelViewer.current) return;

        const { cancel } = modelViewer.current?.loadThinWalls(wallThicknessFileUrl);

        return () => {
            cancel();
        };
    }, [wallThicknessFileUrl]);

    // toggle walls
    useEffect(() => {
        if (modelViewer.current && lastRenderedModelId.current) {
            modelViewer.current.highlightThinWallsOn = wtIsActive;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wtIsActive]);

    return (
        <div
            className={cn([
                styles.box,
                { [styles.expanded]: isFullscreenModeActive, 'viewer-dark-mode-on': isDarkModeActive },
            ])}
            onKeyDown={useCloseOnEscape(toggleFullscreenMode, !isFullscreenModeActive)}
            tabIndex={-1}
        >
            <ModelViewerFullscreenTopPanel
                show={isFullscreenModeActive && !isRotationModeActive}
                toggleFullscreenMode={toggleFullscreenMode}
                contentCustomization={contentCustomization}
                setRenderMode={setCurrentMode}
                modelViewer={modelViewer.current}
                title={title}
                isThumbModeOn={isThumbModeOn} // TODO change to children
                className="small-margin-bottom"
            />

            <div
                className={styles.inner}
                ref={ref => {
                    visualizer = ref as HTMLDivElement;
                }}
            >
                <ModelViewerDarkModeBox
                    show={
                        isFullscreenModeActive && currentMode === ModelViewerRenderMode.Solid && !isRotationModeActive
                    }
                    isDarkModeActive={isDarkModeActive}
                    toggleDarkMode={toggleDarkMode}
                    modelViewer={modelViewer.current}
                />
                <ModelViewerSceneControlsPanel
                    show={
                        isFullscreenModeActive && currentMode === ModelViewerRenderMode.Solid && !isRotationModeActive
                    }
                    contentCustomization={contentCustomization}
                    modelViewer={modelViewer.current}
                />
                <ViewerThumb
                    thumb={model.thumb}
                    smallThumb={model.thumb_300x300}
                    thumbStatus={model.thumb_status}
                    show={isThumbModeOn}
                    expanded={isFullscreenModeActive}
                />

                <ModelViewerRotationDescription isShown={isRotationModeActive} isIqtMode={isIqtMode} />

                <ViewerModePanel
                    expanded={isFullscreenModeActive}
                    toggleFullscreenMode={toggleFullscreenMode}
                    toggleThumbMode={toggleThumbMode}
                    enterRotationMode={enterRotationMode}
                    isRotationActive={isRotationModeActive}
                />
            </div>

            <ModelViewerFullscreenBottomPanel
                show={isRotationModeActive}
                modelId={modelId}
                contentCustomization={contentCustomization}
                modelViewer={modelViewer.current}
                className="small-margin-top"
                exitRotationMode={exitRotationMode}
            />
        </div>
    );
}, viewerPropsAreEqual);
